import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { qk } from '@/lib/query';
import { ApiError } from '@/lib/api';
import { CONNECT_STATUS_KEY, fetchConnectStatus } from '@/api/connect';
import { entriesAlreadyInvoiced } from '@/api/entries';
import * as api from '@/api/invoices';
import { useOptimisticMutation, patchPaged, replaceById, removeById } from './optimistic';
import type { InvoiceDetail, InvoiceStatus, InvoiceSummary, Paginated } from '@/types';
import type { InvoiceListQuery } from '@/api/invoices';

export function useInvoices(query: InvoiceListQuery) {
  return useQuery({
    queryKey: [...qk.invoicesRoot, query] as const,
    queryFn: ({ signal }) => api.fetchInvoices(query, signal),
    placeholderData: keepPreviousData
  });
}

export function useInvoice(id: string | undefined) {
  return useQuery({
    queryKey: qk.invoice(id ?? ''),
    queryFn: ({ signal }) => api.fetchInvoice(id!, signal),
    enabled: Boolean(id)
  });
}

export function useInvoiceEmails(id: string | undefined) {
  return useQuery({
    queryKey: qk.invoiceEmails(id ?? ''),
    queryFn: ({ signal }) => api.fetchInvoiceEmails(id!, signal),
    enabled: Boolean(id)
  });
}

/**
 * Prefetch a row's detail on hover. Combined with the instant HTML preview this
 * makes opening an invoice feel like it was already open — the v1 detail page
 * cost a server render plus a ~10s PDF build.
 */
export function usePrefetchInvoice() {
  const qc = useQueryClient();
  return (id: string) =>
    void qc.prefetchQuery({
      queryKey: qk.invoice(id),
      queryFn: ({ signal }) => api.fetchInvoice(id, signal),
      staleTime: 30_000
    });
}

type ListCache = Paginated<InvoiceSummary>;

/**
 * Status changes patch BOTH the list caches and the detail cache, so going
 * back from a detail screen never shows a stale badge.
 */
export function useSetInvoiceStatus() {
  return useOptimisticMutation<ListCache, { id: string; status: InvoiceStatus }, unknown>({
    mutationFn: ({ id, status }) =>
      status === 'paid'
        ? api.markInvoicePaid(id)
        : status === 'archived'
          ? api.archiveInvoice(id)
          : api.setInvoiceStatus(id, status),
    errorMessage: 'Could not change the invoice status.',
    optimistic: () => ({
      patches: [
        {
          key: qk.invoicesRoot,
          update: (cache: never, vars) => {
            const c = cache as ListCache | InvoiceDetail;
            // qk.invoicesRoot is a prefix of both list and (no) detail keys;
            // guard on shape so a mis-shaped cache is left alone.
            if (!('items' in c)) return cache;
            return patchPaged(c, (items) =>
              replaceById(items, vars.id, {
                status: vars.status,
                paidDate: vars.status === 'paid' ? new Date().toISOString().slice(0, 10) : null
              })
            ) as never;
          }
        },
        {
          key: ['invoice'],
          update: (detail: never, vars) => {
            const d = detail as InvoiceDetail;
            if (d?.id !== vars.id) return detail;
            return {
              ...d,
              status: vars.status,
              paidDate: vars.status === 'paid' ? new Date().toISOString().slice(0, 10) : null
            } as never;
          }
        }
      ],
      invalidate: [qk.invoicesRoot, qk.reportsRoot]
    }),
    successMessage: (_r, vars) =>
      vars.status === 'paid' ? 'Marked as paid' : `Moved to ${vars.status}`
  });
}

export function useDiscardInvoice() {
  return useOptimisticMutation<ListCache, { id: string }, unknown>({
    mutationFn: ({ id }) => api.discardInvoice(id),
    errorMessage: 'Could not discard that draft.',
    optimistic: () => ({
      patches: [
        {
          key: qk.invoicesRoot,
          update: (cache: never, vars) => {
            const c = cache as ListCache;
            if (!('items' in c)) return cache;
            return patchPaged(c, (items) => removeById(items, vars.id)) as never;
          }
        }
      ],
      invalidate: [qk.invoicesRoot, qk.daycardsRoot, qk.uninvoicedEntriesRoot, qk.reportsRoot]
    }),
    successMessage: 'Draft discarded — its time entries are billable again'
  });
}

export function useDeleteInvoice() {
  return useOptimisticMutation<ListCache, { id: string }, unknown>({
    mutationFn: ({ id }) => api.deleteInvoice(id),
    errorMessage: 'Could not delete that invoice.',
    optimistic: () => ({
      patches: [
        {
          key: qk.invoicesRoot,
          update: (cache: never, vars) => {
            const c = cache as ListCache;
            if (!('items' in c)) return cache;
            return patchPaged(c, (items) => removeById(items, vars.id)) as never;
          }
        }
      ],
      invalidate: [qk.invoicesRoot, qk.daycardsRoot, qk.uninvoicedEntriesRoot, qk.reportsRoot]
    }),
    successMessage: 'Invoice deleted'
  });
}

/**
 * The invoice editor stages its changes and saves once. The optimistic patch
 * writes the whole edited detail into the cache so the preview re-renders
 * before the network call returns.
 */
export function useSaveInvoice() {
  return useOptimisticMutation<InvoiceDetail, { id: string; next: InvoiceDetail; patch: api.InvoicePatch }, unknown>({
    mutationFn: ({ id, patch }) => api.updateInvoice(id, patch),
    errorMessage: 'Those invoice changes could not be saved.',
    optimistic: ({ id }) => ({
      patches: [
        {
          key: qk.invoice(id),
          exact: true,
          update: (_current: never, vars) => vars.next as never
        }
      ],
      invalidate: [
        qk.invoice(id),
        qk.invoicesRoot,
        qk.daycardsRoot,
        qk.uninvoicedEntriesRoot,
        qk.creditsAndFeesRoot
      ]
    }),
    successMessage: 'Invoice saved'
  });
}

export function useSetInvoiceTemplate() {
  return useOptimisticMutation<InvoiceDetail, { id: string; template: string }, unknown>({
    mutationFn: ({ id, template }) => api.setInvoiceTemplate(id, template),
    errorMessage: 'Could not switch the template.',
    optimistic: ({ id }) => ({
      patches: [
        {
          key: qk.invoice(id),
          exact: true,
          update: (detail: never, vars) =>
            ({ ...(detail as InvoiceDetail), templateId: vars.template }) as never
        }
      ],
      invalidate: [qk.invoice(id)]
    })
  });
}

/**
 * Where a submitted bank transfer has got to. Fetched once per page open, and
 * only while the invoice is processing: there is nothing to watch minute to
 * minute, and refetching would keep the database awake.
 */
export function useBankTransferStatus(id: string | undefined, enabled: boolean) {
  return useQuery({
    queryKey: [...qk.invoice(id ?? ''), 'bank-transfer-status'],
    queryFn: () => api.fetchBankTransferStatus(id!),
    enabled: enabled && !!id,
    staleTime: 60_000,
    refetchOnWindowFocus: false
  });
}

/**
 * Cancel a client's bank transfer that is stuck waiting on bank confirmation.
 * The server refuses once the debit has started, and says so in a sentence.
 */
export function useCancelBankTransfer() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.cancelBankTransfer(id),
    onSuccess: (_data, id) => {
      toast.success('Bank transfer cancelled');
      void qc.invalidateQueries({ queryKey: qk.invoice(id) });
      void qc.invalidateQueries({ queryKey: qk.invoicesRoot });
    },
    onError: (err, id) => {
      toast.error(
        err instanceof ApiError && err.status < 500
          ? err.message
          : 'Could not cancel the bank transfer.'
      );
      void qc.invalidateQueries({ queryKey: qk.invoice(id) });
    }
  });
}

// ------------------------------------------------------------ payment links

/**
 * Whether this team can be paid through Billable at all.
 *
 * `GET /api/stripe/connect/status` answers 200 with `enabled: false` when the
 * flag is off rather than 404ing, so a disabled deployment is a normal render
 * and not an error state. The status is one Stripe account lookup server-side,
 * so it is cached hard rather than refetched per screen.
 */
export function useConnectStatus() {
  return useQuery({
    queryKey: CONNECT_STATUS_KEY,
    queryFn: ({ signal }) => fetchConnectStatus(signal),
    staleTime: 5 * 60_000
  });
}

/** The payment-link affordances only mean anything once money can move. */
export function useCanTakePayments() {
  const status = useConnectStatus().data;
  return Boolean(status?.enabled && status.connected && status.chargesEnabled);
}

/**
 * Copy text, and say so. `navigator.clipboard` is absent on an insecure origin
 * and `writeText` rejects when the permission is denied, so both have to be
 * handled — the old code swallowed the rejection and claimed success anyway.
 */
export async function copyToClipboard(text: string, label: string) {
  try {
    if (!navigator.clipboard) throw new Error('Clipboard unavailable');
    await navigator.clipboard.writeText(text);
    toast.success(`${label} copied`);
    return true;
  } catch {
    toast.error(`Could not copy the ${label.toLowerCase()}.`, { description: text });
    return false;
  }
}

/**
 * Create the link and put it on the clipboard. Errors are shown as the server
 * wrote them — 'Could not create a payment link.' was the only thing on screen
 * while this was posting to a route that did not exist.
 */
export function useCreatePaymentLink() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.createPaymentLink(id),
    async onSuccess(result, id) {
      qc.setQueryData<InvoiceDetail>(qk.invoice(id), (detail) =>
        detail ? { ...detail, paymentLinkUrl: result.url } : detail
      );
      await copyToClipboard(result.url, 'Payment link');
    },
    onError(err: unknown) {
      toast.error(err instanceof ApiError ? err.message : 'Could not create a payment link.');
    }
  });
}

/**
 * Billable sends the invoice itself, through Resend. One click, no body — the
 * server owns the template, the recipients and the pay link.
 *
 * The 501 `EMAIL_NOT_CONFIGURED` case is deliberately NOT toasted: it is the
 * state of any deployment without a RESEND_API_KEY, and the server answers it
 * with a full preview of the message. The detail page reads that off
 * `mutation.error` and shows it, so a red toast would be both wrong and in the
 * way. Every other failure toasts the server's own sentence — those were
 * written to be read by whoever pressed the button.
 */
export function useSendInvoice() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.sendInvoice(id),
    onSuccess(result, id) {
      // The server only writes `sent` after Resend accepted the message, so
      // patching the cache here is reporting a fact, not a guess.
      qc.setQueryData<InvoiceDetail>(qk.invoice(id), (detail) =>
        detail ? { ...detail, status: 'sent' } : detail
      );
      void qc.invalidateQueries({ queryKey: qk.invoice(id) });
      void qc.invalidateQueries({ queryKey: qk.invoiceEmails(id) });
      void qc.invalidateQueries({ queryKey: qk.emailsRoot });
      void qc.invalidateQueries({ queryKey: qk.invoicesRoot });
      toast.success('Invoice sent', {
        description: result.sentTo.length ? `To ${result.sentTo.join(', ')}` : undefined
      });
    },
    onError(err: unknown) {
      if (api.emailNotConfigured(err)) return;
      // A demo team may only mail its own contact address; the server's 403
      // names that address, so its sentence is the toast. `ApiError.message`
      // carries it whichever error shape the route used.
      const demoBlocked = err instanceof ApiError && err.code === 'demo_recipient';
      toast.error(
        demoBlocked ? err.message : api.invoiceErrorText(err, 'Could not send the invoice.')
      );
    }
  });
}

export interface MailClientHandoff {
  id: string;
  subject: string;
  body: string;
  to: string[];
  cc?: string[];
  bcc?: string[];
  /** False when the invoice is already `sent` and there is nothing to change. */
  markSent: boolean;
}

/**
 * The bookkeeping half of the manual path: record the message Billable
 * composed, then move the invoice to `sent` (matching v1).
 *
 * WHAT THE LOG ROW CLAIMS. `sentVia: 'mail_client'` means Billable composed
 * this message and handed it to the user's mail app — nothing more. It cannot
 * know whether the compose window was ever sent or was closed unread, and the
 * emails list renders that case distinctly, so the row is a record of a handoff
 * rather than of a delivery.
 *
 * ORDERING. The caller opens the mailto FIRST and calls this second, which is
 * v1's follow-up ordering rather than v1's invoice ordering (v1 does it both
 * ways round in two files). Two reasons, in this order: the anchor click has to
 * happen synchronously inside the user's click handler or the browser treats
 * the `_blank` open as an unsolicited popup, and a row written after the fact
 * describes a handoff that demonstrably happened instead of one we intended.
 */
export function useMailClientHandoff() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (vars: MailClientHandoff) => {
      await api.logInvoiceEmail(vars.id, {
        subject: vars.subject,
        emailBody: vars.body,
        sentTo: vars.to,
        cc: vars.cc,
        bcc: vars.bcc,
        emailType: 'invoice',
        sentVia: 'mail_client'
      });
      if (vars.markSent) await api.setInvoiceStatus(vars.id, 'sent');
      return vars;
    },
    onSuccess(vars) {
      qc.setQueryData<InvoiceDetail>(qk.invoice(vars.id), (detail) =>
        detail && vars.markSent ? { ...detail, status: 'sent' } : detail
      );
      void qc.invalidateQueries({ queryKey: qk.invoice(vars.id) });
      void qc.invalidateQueries({ queryKey: qk.invoiceEmails(vars.id) });
      void qc.invalidateQueries({ queryKey: qk.emailsRoot });
      void qc.invalidateQueries({ queryKey: qk.invoicesRoot });
      toast.success(vars.markSent ? 'Marked sent' : 'Logged', {
        description: 'Billable recorded that it handed the message to your email app.'
      });
    },
    onError(err: unknown) {
      toast.error(
        api.invoiceErrorText(err, 'Your email app has the message, but Billable could not record it.')
      );
    }
  });
}

export function useSendFollowUp() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.sendFollowUp(id),
    onSuccess(_r, id) {
      void qc.invalidateQueries({ queryKey: qk.invoiceEmails(id) });
      void qc.invalidateQueries({ queryKey: qk.emailsRoot });
      toast.success('Follow-up sent');
    },
    onError() {
      toast.error('Could not send the follow-up.');
    }
  });
}

export interface CreateInvoiceVars extends api.CreateInvoiceInput {
  /**
   * Skip the 'Invoice created' toast. "Create and send" hands the new invoice
   * to the detail page, which sends it and toasts 'Invoice sent'; a second
   * toast for the create step in between is noise, not news.
   */
  silent?: boolean;
}

export function useCreateInvoice() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ silent: _silent, ...input }: CreateInvoiceVars) => api.createInvoice(input),
    onSuccess(_invoice, vars) {
      void qc.invalidateQueries({ queryKey: qk.invoicesRoot });
      void qc.invalidateQueries({ queryKey: qk.daycardsRoot });
      void qc.invalidateQueries({ queryKey: qk.uninvoicedEntriesRoot });
      void qc.invalidateQueries({ queryKey: qk.creditsAndFeesRoot });
      void qc.invalidateQueries({ queryKey: qk.setup });
      if (!vars.silent) toast.success('Invoice created');
    },
    onError(error: unknown, vars) {
      // The server refused because the list the user picked from was stale:
      // some of those entries went onto another invoice since it loaded.
      // Refetch it so the screen shows what is actually still billable.
      if (entriesAlreadyInvoiced(error)) {
        void qc.invalidateQueries({ queryKey: qk.uninvoicedEntries(vars.clientId) });
        toast.error('Some entries were already invoiced. The list has been refreshed.');
        return;
      }
      toast.error('Could not create the invoice.', {
        description: api.invoiceErrorText(error, error instanceof Error ? error.message : '')
      });
    }
  });
}

export function useDownloadExcel() {
  return useMutation({
    mutationFn: ({ id, invoiceNumber }: { id: string; invoiceNumber: string }) =>
      api.downloadInvoiceExcel(id, invoiceNumber),
    onError() {
      toast.error('Could not generate the spreadsheet.');
    }
  });
}

// --------------------------------------------------------- quick invoicing

export function useTentativeInvoices() {
  return useQuery({
    queryKey: qk.quickInvoices,
    queryFn: ({ signal }) => api.fetchTentativeInvoices(signal),
    staleTime: 30_000
  });
}

export function useRegenerateTentative() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: api.regenerateTentativeInvoices,
    onSuccess(bundle) {
      qc.setQueryData(qk.quickInvoices, bundle);
      toast.success(
        bundle.invoices.length
          ? `${bundle.invoices.length} invoices ready to review`
          : 'No uninvoiced time found'
      );
    },
    onError() {
      toast.error('Could not regenerate the invoices.');
    }
  });
}

export function useConvertTentative() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: api.convertTentativeInvoice,
    async onMutate({ tentativeInvoice }) {
      const id = (tentativeInvoice as { id?: string }).id;
      await qc.cancelQueries({ queryKey: qk.quickInvoices });
      const previous = qc.getQueryData<api.TentativeBundle>(qk.quickInvoices);
      // Drop the invoice from the queue immediately so the user can move on.
      if (previous && id) {
        qc.setQueryData<api.TentativeBundle>(qk.quickInvoices, {
          ...previous,
          invoices: previous.invoices.filter((i) => i.id !== id)
        });
      }
      return { previous };
    },
    onError(_e, _v, ctx) {
      if (ctx?.previous) qc.setQueryData(qk.quickInvoices, ctx.previous);
      toast.error('Could not save that invoice.');
    },
    onSuccess() {
      void qc.invalidateQueries({ queryKey: qk.invoicesRoot });
      void qc.invalidateQueries({ queryKey: qk.daycardsRoot });
      void qc.invalidateQueries({ queryKey: qk.uninvoicedEntriesRoot });
      // Converting is what attaches the credits/fees it was showing.
      void qc.invalidateQueries({ queryKey: qk.creditsAndFeesRoot });
    }
  });
}

export type { InvoiceListQuery };
